import { Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { JJamesCardComponent } from 'shared-lib';
import { CreateDeviceGroupRequest, DeviceGroupDto, DeviceGroupService, DeviceGroupType, InfrastructureDto, InfrastructureService } from '../../../../api/gen';
import { firstValueFrom } from 'rxjs';

export interface DeviceGroupZoneConfigData {
  infrastructure: InfrastructureDto;
}

@Component({
  selector: 'app-device-group-zone-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatListModule,
    MatProgressBarModule,
    JJamesCardComponent
  ],
  templateUrl: './device-group-zone-config.component.html',
  styleUrls: ['./device-group-zone-config.component.scss']
})
export class DeviceGroupZoneConfigComponent implements OnInit {
  infrastructure: InfrastructureDto;
  configuredDeviceGroups = signal<DeviceGroupDto[]>([]);
  isSaving = signal(false);
  isLoading = signal(true);

  newGroupForm: FormGroup;
  deviceGroupTypes = Object.values(DeviceGroupType);

  constructor(
    private dialogRef: MatDialogRef<DeviceGroupZoneConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeviceGroupZoneConfigData,
    private fb: FormBuilder,
    private deviceGroupService: DeviceGroupService,
    private infrastructureService: InfrastructureService,
    private snackBar: MatSnackBar
  ) {
    this.infrastructure = data.infrastructure;
    this.newGroupForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [DeviceGroupType.Gateway, Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadConfiguredDeviceGroups();
  }

  /**
   * Always reads the list back from the server. The dialog is opened with a snapshot of the
   * infrastructure taken before the first create, so trusting it left the list (and this component's copy
   * of `infrastructure`) stale the moment a group was added — which is why a second group could only be
   * added after closing and reopening the dialog.
   */
  async loadConfiguredDeviceGroups(): Promise<void> {
    this.isLoading.set(true);
    try {
      const fresh = await firstValueFrom(
        this.infrastructureService.infrastructuresInfrastructureIdGet(this.infrastructure.id!)
      );
      this.infrastructure = fresh;
      this.configuredDeviceGroups.set(fresh.deviceGroups ?? []);
    } catch (error) {
      console.error('Error loading device groups:', error);
      this.configuredDeviceGroups.set(this.infrastructure.deviceGroups ?? []);
      this.snackBar.open(
        `Showing the list from when this dialog opened — refresh failed: ${this.errorText(error)}`,
        'Close', { duration: 5000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  /** MadResult errors arrive as a plain string body, not JSON — show it instead of a generic message. */
  private errorText(error: unknown): string {
    const err = error as { error?: unknown; message?: string; status?: number };
    if (typeof err?.error === 'string' && err.error.trim()) return err.error;
    if (err?.status) return `HTTP ${err.status}`;
    return err?.message ?? 'unknown error';
  }

  async onAddNewGroup(): Promise<void> {
    if (this.newGroupForm.invalid) {
      this.newGroupForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const formValue = this.newGroupForm.value;

    const request: CreateDeviceGroupRequest = {
      groupName: formValue.name,
      groupDescription: formValue.description,
      groupType: formValue.type,
      infrastructureId: this.infrastructure.id!
    };

    try {
      await firstValueFrom(this.deviceGroupService.deviceGroupsPost(request as any)) as DeviceGroupDto;

      this.snackBar.open('Device group created and assigned successfully.', 'Close', { duration: 3000 });
      this.newGroupForm.reset({ type: DeviceGroupType.Gateway });
      // Re-read from the server rather than appending locally, so the next create starts from the real
      // state of the infrastructure instead of a snapshot that is now one group behind.
      await this.loadConfiguredDeviceGroups();
    } catch (error) {
      console.error('Error creating or assigning device group:', error);
      this.snackBar.open(`Failed to create device group: ${this.errorText(error)}`, 'Close', { duration: 8000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  async removeGroup(deviceGroupId: string): Promise<void> {
    try {
      await firstValueFrom(this.infrastructureService.infrastructuresInfrastructureIdDeviceGroupsDeviceGroupIdDelete(this.infrastructure.id!, deviceGroupId));
      this.configuredDeviceGroups.update(groups => groups.filter(g => g.id !== deviceGroupId));
      this.snackBar.open('Device group unassigned successfully.', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Error unassigning device group:', error);
      this.snackBar.open('Failed to unassign device group.', 'Close', { duration: 5000 });
    }
  }

  onClose(): void {
    this.dialogRef.close(true);
  }
}
